import type { Dispatch } from 'redux';
import type { AspectRatio, OpaqueVideoData } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type {
    DisplayGroup,
    SceneAttributesRecommendation,
    SceneDesign,
    Template,
} from 'js/src/client';
import { ApiService, TemplateStatusEnum, Urls } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { MODAL_IDS, showModal } from 'js/src/data/modals';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import StoreORM from 'js/src/data/store-orm';
import {
    APIRequestStatus,
    isDisplayGroupsListRequestFetched,
    isSceneDesignsListRequestFetched,
    isTemplateCategoriesListRequestFetched,
    isTemplateRetrieveRequestFetched,
    setDisplayGroupsListRequestStatus,
    setSceneDesignsListRequestStatus,
    setTemplateCategoriesListRequestStatus,
    setTemplateRetrieveRequestStatus,
    setTemplatesListRequestStatus,
} from 'js/src/data/templates/slices/loadingInfoSlice';
import {
    selectSceneSequenceDesignsLoadingStatus,
    setSceneSequenceDesigns,
    setSceneSequenceDesignsFailed,
    setSceneSequenceDesignsLoading,
} from 'js/src/data/templates/slices/sceneSequenceDesignsSlice';
import * as actionCreators from 'js/src/data/templates/templates-actions';
import {
    setSceneDesignOrders,
    setTemplateOrders,
    storeSceneDesigns,
    storeTemplate,
    storeTemplateCategories,
    storeTemplates,
} from 'js/src/data/templates/templates-actions';
import {
    areAllSceneDesignsLoadedForTemplate,
    areTemplatesLoaded,
    getDisplayGroupById,
    getDraftCategoryId,
    getLastSceneDesignOrder,
    getSceneDesign,
    getSupportedAspectRatios,
    getTemplateBundle,
    getTemplateById,
    getTemplateData,
    getTemplateDisplayGroupsAmount,
    getTemplateIds,
    getTemplateSceneIds,
    hasTemplate,
    hasTemplateDisplayGroups,
} from 'js/src/data/templates/templates-selectors';
import { joinOrStart, startFresh } from 'js/src/data/utils/inFlightRequests';
import {
    getEditorAppActiveSceneGroupIndex,
    getEditorAppActiveSceneIndex,
} from 'js/src/editor/editor-app-reducer';
import {
    findStandardTransitionNameByUrl,
    intelligentlyResizeSceneToAspectRatio,
} from 'js/src/editor/util';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Flags from 'js/src/libs/flags';
import type { AppDispatch, GetRootState, RootState } from 'js/src/libs/RootState';
import { withDataLuminarySync } from 'js/src/luminary';
import { buildSceneSequenceDesigns } from 'js/src/luminary/scene-sequence-designs';
import { isApiError } from 'js/src/types';

const PREFERRED_RESCALE_ASPECT_RATIO = VideoData.VERTICAL_ASPECT_RATIO;

export function loadTemplates() {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        if (areTemplatesLoaded(getState())) {
            return;
        }
        await joinOrStart(getState, 'templates', async () => {
            try {
                dispatch(
                    setTemplatesListRequestStatus({
                        status: APIRequestStatus.FETCHING,
                    }),
                );
                const response = await ApiService.templatesList();
                const templates = response.data;
                dispatch(storeTemplates({ templates }));
                dispatch(
                    setTemplatesListRequestStatus({
                        status: APIRequestStatus.FETCHED,
                        data: templates,
                    }),
                );
            } catch (error) {
                dispatch(
                    setTemplatesListRequestStatus({
                        status: APIRequestStatus.FAILED,
                    }),
                );
                throw error;
            }
        });
    };
}

export const loadTemplate = (templateId: string) => {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        if (hasTemplate(getState(), templateId)) {
            return;
        }
        if (isTemplateRetrieveRequestFetched(getState(), templateId)) {
            return;
        }
        await joinOrStart(getState, `template:${templateId}`, async () => {
            try {
                dispatch(
                    setTemplateRetrieveRequestStatus({
                        id: templateId,
                        status: APIRequestStatus.FETCHING,
                    }),
                );
                const response = await ApiService.templatesRetrieve({ path: { id: templateId } });
                const template = response.data;
                dispatch(storeTemplate({ template }));
                dispatch(
                    setTemplateRetrieveRequestStatus({
                        id: templateId,
                        status: APIRequestStatus.FETCHED,
                        data: template,
                    }),
                );
            } catch (error) {
                dispatch(
                    setTemplateRetrieveRequestStatus({
                        id: templateId,
                        status: APIRequestStatus.FAILED,
                    }),
                );
                throw error;
            }
        });
    };
};

export function loadTemplateCategories() {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        if (isTemplateCategoriesListRequestFetched(getState())) {
            return;
        }
        await joinOrStart(getState, 'templateCategories', async () => {
            try {
                dispatch(
                    setTemplateCategoriesListRequestStatus({
                        status: APIRequestStatus.FETCHING,
                    }),
                );
                const response = await ApiService.templateCategoriesList();
                dispatch(storeTemplateCategories({ categories: response.data }));
                dispatch(
                    setTemplateCategoriesListRequestStatus({
                        status: APIRequestStatus.FETCHED,
                    }),
                );
            } catch (error) {
                dispatch(
                    setTemplateCategoriesListRequestStatus({
                        status: APIRequestStatus.FAILED,
                    }),
                );
                throw error;
            }
        });
    };
}

export function loadTemplatesAndCategories() {
    return (dispatch: AppDispatch) => {
        return Promise.all([dispatch(loadTemplates()), dispatch(loadTemplateCategories())]);
    };
}

export function loadAllSceneDesignIdsPerTemplate() {
    return (dispatch: AppDispatch) => {
        return new Promise<void>((resolve) => {
            const fetchAndStore = async () => {
                const response = await ApiService.sceneDesignsIdsPerTemplateList();
                dispatch(
                    actionCreators.storeAllSceneDesignIdsPerTemplate({
                        allSceneDesignIdsPerTemplate: response.data,
                    }),
                );
                resolve();
            };
            fetchAndStore();
        });
    };
}

export function loadSceneDesignsForTemplate(templateId: string) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        if (areAllSceneDesignsLoadedForTemplate(getState(), templateId)) {
            return;
        }
        if (isSceneDesignsListRequestFetched(getState(), templateId)) {
            return;
        }
        await joinOrStart(getState, `sceneDesigns:${templateId}`, async () => {
            try {
                dispatch(
                    setSceneDesignsListRequestStatus({
                        id: templateId,
                        status: APIRequestStatus.FETCHING,
                    }),
                );
                const response = await ApiService.sceneDesignsList({
                    query: { template_id: templateId },
                });
                const sceneDesigns = response.data;
                dispatch(storeSceneDesigns({ sceneDesigns, templateId }));
                dispatch(
                    setSceneDesignsListRequestStatus({
                        id: templateId,
                        status: APIRequestStatus.FETCHED,
                        data: sceneDesigns,
                    }),
                );
            } catch (error) {
                dispatch(
                    setSceneDesignsListRequestStatus({
                        id: templateId,
                        status: APIRequestStatus.FAILED,
                    }),
                );
                throw error;
            }
        });
    };
}

export function loadDisplayGroupsForTemplate(templateId: string, { skipIfLoaded = true } = {}) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        if (skipIfLoaded) {
            if (hasTemplateDisplayGroups(getState(), templateId)) {
                return;
            }
            if (isDisplayGroupsListRequestFetched(getState(), templateId)) {
                return;
            }
        }
        const fetchAndStore = async () => {
            try {
                dispatch(
                    setDisplayGroupsListRequestStatus({
                        id: templateId,
                        status: APIRequestStatus.FETCHING,
                    }),
                );
                const response = await ApiService.templatesDisplayGroupsList({
                    path: { template_pk: templateId },
                });
                dispatch(
                    actionCreators.storeDisplayGroups({ templateId, displayGroups: response.data }),
                );
                dispatch(
                    setDisplayGroupsListRequestStatus({
                        id: templateId,
                        status: APIRequestStatus.FETCHED,
                    }),
                );
            } catch (error) {
                dispatch(
                    setDisplayGroupsListRequestStatus({
                        id: templateId,
                        status: APIRequestStatus.FAILED,
                    }),
                );
                throw error;
            }
        };
        const key = `displayGroups:${templateId}`;
        // A forced refetch issues its own request rather than joining one that may predate the
        // caller's mutation. It does not supersede that request, which still wins if it lands second.
        await (skipIfLoaded
            ? joinOrStart(getState, key, fetchAndStore)
            : startFresh(getState, key, fetchAndStore));
    };
}

export function updateDisplayGroupForTemplate(
    templateId: string,
    newDisplayGroup: ImmutableMap<DisplayGroup>,
) {
    const displayGroupData = {
        display_name: newDisplayGroup.get('display_name'),
        order: newDisplayGroup.get('order'),
    };
    return (dispatch: AppDispatch) => {
        return new Promise<void>((resolve, reject) => {
            ApiService.templatesDisplayGroupsPartialUpdate({
                path: {
                    id: newDisplayGroup.get('id'),
                    template_pk: templateId,
                },
                body: displayGroupData,
            })
                .then(() => {
                    Toast.success('Display group name saved');
                    resolve();
                })
                .catch((error: unknown) => {
                    if (isApiError(error)) {
                        Toast.error(error.body as string);
                    } else {
                        Toast.error((error as Error).message);
                    }
                    reject(error);
                });
        });
    };
}

export function copyDisplayGroupToTemplate(oldDisplayGroupId: number, newTemplateId: string) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const displayGroup = getDisplayGroupById(getState(), oldDisplayGroupId)!
            .delete('id')
            .set('template', newTemplateId);

        return ApiService.templatesDisplayGroupsCreate({
            path: { template_pk: newTemplateId },
            body: displayGroup.toJS(),
        });
    };
}

export function createDisplayGroupsForTemplate(templateId: string) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const displayGroupAmount = getTemplateDisplayGroupsAmount(getState(), templateId);
        const defaultDisplayGroup: {
            display_name: string;
            template: string;
            order: number | null;
        } = {
            display_name: `New display group #${displayGroupAmount}`,
            template: templateId,
            order: null,
        };

        return new Promise<void>((resolve, reject) => {
            ApiService.templatesDisplayGroupsCreate({
                path: { template_pk: templateId },
                body: defaultDisplayGroup,
            })
                .then(() => {
                    dispatch(loadDisplayGroupsForTemplate(templateId, { skipIfLoaded: false }));
                    resolve();
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    reject(error);
                });
        });
    };
}

export function updateAndSaveDisplayGroupOrders(reorderedIds: string[], templateId: string) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const data = reorderedIds.map((id, newOrder) => {
            return {
                id: parseInt(id),
                order: newOrder,
            };
        });
        return new Promise((resolve, reject) => {
            ApiService.templatesDisplayGroupsOrderPartialUpdate({
                path: { template_pk: templateId },
                body: data,
            })
                .then((response) => {
                    dispatch(loadDisplayGroupsForTemplate(templateId, { skipIfLoaded: false }));
                    resolve(response);
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    reject(error);
                });
        });
    };
}

/**
 * Loads templates and its scene designs if they haven't yet been loaded.
 * Since this is a "critical path" request (the app doesn't work if it fails) we'll retry transient errors.
 */
export function loadSceneSequenceDesignsForTemplate(templateId: string) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        if (!Flags.canUseMorphTransitions()) {
            return;
        }
        const status = selectSceneSequenceDesignsLoadingStatus(getState(), templateId);
        if (status === 'loaded' || status === 'loading') return;

        dispatch(setSceneSequenceDesignsLoading({ templateId }));
        try {
            const designs = await buildSceneSequenceDesigns(
                templateId,
                getTemplateBundle(getState(), templateId),
            );
            dispatch(setSceneSequenceDesigns({ templateId, designs }));
        } catch {
            dispatch(setSceneSequenceDesignsFailed({ templateId }));
        }
    };
}

/**
 * Loads everything getTemplateBundle needs, fetching only what is missing. Prefer
 * loadTemplateAndSceneDesigns for a template the page is built around — it loads the display groups
 * too and reports a failure to the user; this one leaves that to the caller.
 */
export function loadTemplateBundle(templateId: string) {
    return async (dispatch: AppDispatch) => {
        await Promise.all([
            dispatch(loadTemplate(templateId)),
            dispatch(loadSceneDesignsForTemplate(templateId)),
        ]);
    };
}

export function loadTemplateAndSceneDesigns(templateId: string) {
    return (dispatch: AppDispatch) => {
        return new Promise<void>((resolve, reject) => {
            const fetchAndStore = async (retries: number = 3) => {
                try {
                    await Promise.all([
                        dispatch(loadTemplateBundle(templateId)),
                        dispatch(loadDisplayGroupsForTemplate(templateId)),
                    ]);
                    void dispatch(loadSceneSequenceDesignsForTemplate(templateId));
                    resolve();
                } catch (err) {
                    if (isApiError(err) && err.status === 404) {
                        dispatch(
                            showModal({
                                modal_id: MODAL_IDS.GENERIC_ERROR,
                                modal_context: ImmutableFromJS({
                                    title: 'Oops!',
                                    text: `Error loading template "${templateId}". This template may no longer exist, or you might not have access to it.`,
                                    onClose: () => {
                                        window.location.href = Urls.APP;
                                    },
                                }),
                            }),
                        );
                    } else if (isApiError(err) && retries > 0) {
                        await fetchAndStore(retries - 1);
                    } else {
                        ErrorReporter.capture(err);
                        dispatch(
                            showModal({
                                modal_id: MODAL_IDS.GENERIC_ERROR,
                                modal_context: ImmutableFromJS({
                                    title: 'Oops!',
                                    text: `Error loading template "${templateId}". Are you connected to the internet?`,
                                    onClose: () => {
                                        window.location.reload();
                                    },
                                }),
                            }),
                        );
                        reject(err);
                    }
                }
            };
            void fetchAndStore();
        });
    };
}

export function updateSceneDesignAttributesRecommendation(
    attributesRecommendation: SceneAttributesRecommendation,
    sceneDesignId: string,
) {
    return (dispatch: AppDispatch) => {
        dispatch(
            actionCreators.setSceneDesignAttributesRecommendation({
                attributesRecommendation,
                sceneDesignId,
            }),
        );
    };
}

/**
 * Duplicate the given scene design to same or different template.
 *
 * @param {string} oldSceneDesignId e.g. be_yourself:text_1
 * @param {string} newSceneDesignId e.g. be_yourself:text_6, abb:text_6
 * @param {function} getDisplayGroup function which maps an old display group id to new display group id
 */
export function duplicateSceneDesign(
    oldSceneDesignId: string,
    newSceneDesignId: string,
    aspectRatiosToDuplicate: AspectRatio[],
    getDisplayGroup?: (oldSceneGroup: number) => number,
    preserveOrder: boolean = false,
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const oldTemplateId = VideoData.getTemplateIdFromSceneDesignId(oldSceneDesignId);
        const newTemplateId = VideoData.getTemplateIdFromSceneDesignId(newSceneDesignId);

        const oldTemplateData = getTemplateData(getState(), oldTemplateId)!.toJS();
        const newTemplateData = getTemplateData(getState(), newTemplateId)!.toJS();

        const oldTemplateSupportedAspectRatios = getSupportedAspectRatios(
            getState(),
            oldTemplateId,
        );
        const sourceAspectRatio = oldTemplateSupportedAspectRatios.includes(
            PREFERRED_RESCALE_ASPECT_RATIO,
        )
            ? PREFERRED_RESCALE_ASPECT_RATIO
            : oldTemplateSupportedAspectRatios[0];

        const originalSceneDesign: SceneDesign = getSceneDesign(getState(), oldSceneDesignId)!;
        const sceneDesign: SceneDesign = { ...originalSceneDesign };
        sceneDesign.template = newTemplateId;
        sceneDesign.id = newSceneDesignId;
        sceneDesign.status = TemplateStatusEnum.DRAFT;

        VideoData.ALL_ASPECT_RATIOS.forEach((aspectRatio) => {
            const key = `${aspectRatio}_data` as keyof SceneDesign;
            if (!aspectRatiosToDuplicate.includes(aspectRatio)) {
                (sceneDesign as Record<string, unknown>)[key] = null;
                return;
            }

            if (!oldTemplateSupportedAspectRatios.includes(aspectRatio)) {
                const sourceKey = `${sourceAspectRatio}_data` as keyof SceneDesign;
                const sourceData = originalSceneDesign[sourceKey] as OpaqueVideoData;

                const duplicatedData = dispatch(
                    duplicateAndResizeSceneData(
                        sourceAspectRatio,
                        aspectRatio,
                        sourceData,
                        oldTemplateData,
                        newSceneDesignId,
                        newTemplateData,
                    ),
                );

                (sceneDesign as Record<string, unknown>)[key] = duplicatedData;
                return;
            }

            const aspectRatioData = originalSceneDesign[key];
            const duplicatedData = duplicateSceneData(
                aspectRatio,
                aspectRatioData as OpaqueVideoData,
                oldTemplateData,
                newSceneDesignId,
                newTemplateData,
            );
            (sceneDesign as Record<string, unknown>)[key] = duplicatedData;
        });

        const attributesRecommendation = sceneDesign.scene_attributes_recommendation;
        const originalRecommendedTextPosition =
            attributesRecommendation[`text_position_${sourceAspectRatio}`];

        VideoData.ALL_ASPECT_RATIOS.forEach((aspectRatio) => {
            if (oldTemplateSupportedAspectRatios.includes(aspectRatio)) {
                return;
            }

            const textPositionKey = `text_position_${aspectRatio}`;

            (attributesRecommendation as Record<string, unknown>)[textPositionKey] =
                originalRecommendedTextPosition;
        });

        if (getDisplayGroup && sceneDesign.display_group) {
            sceneDesign.display_group = getDisplayGroup(sceneDesign.display_group);
        }

        if (!preserveOrder) {
            sceneDesign.order = getLastSceneDesignOrder(getState(), newTemplateId) + 1;
        }

        return new Promise<void>((resolve, reject) => {
            ApiService.sceneDesignsCreate({ body: sceneDesign })
                .then((response) => {
                    dispatch(actionCreators.storeSceneDesign({ sceneDesign: response.data }));
                    resolve();
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    reject(error);
                });
        });
    };
}

export function overwriteSceneDesign(sourceSceneDesignId: string, targetSceneDesignId: string) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const sourceTemplateId = VideoData.getTemplateIdFromSceneDesignId(sourceSceneDesignId);
        const targetTemplateId = VideoData.getTemplateIdFromSceneDesignId(targetSceneDesignId);

        const sourceTemplateData = getTemplateData(getState(), sourceTemplateId)!.toJS();
        const targetTemplateData = getTemplateData(getState(), targetTemplateId)!.toJS();

        const sourceSceneDesign: SceneDesign = getSceneDesign(getState(), sourceSceneDesignId)!;
        const targetSceneDesign: SceneDesign = getSceneDesign(getState(), targetSceneDesignId)!;

        const sceneDesign: SceneDesign = { ...targetSceneDesign };

        VideoData.ALL_ASPECT_RATIOS.forEach((aspectRatio) => {
            const key = `${aspectRatio}_data` as keyof SceneDesign;
            const sourceAspectRatioData = sourceSceneDesign[key] as OpaqueVideoData;

            if (!sourceAspectRatioData) {
                (sceneDesign as Record<string, unknown>)[key] = null;
                return;
            }

            const duplicatedData = duplicateSceneData(
                aspectRatio,
                sourceAspectRatioData,
                sourceTemplateData,
                targetSceneDesignId,
                targetTemplateData,
                true, // preserveCustomTransition
            );
            (sceneDesign as Record<string, unknown>)[key] = duplicatedData;
        });

        sceneDesign.scene_attributes_recommendation =
            sourceSceneDesign.scene_attributes_recommendation;

        return new Promise<void>((resolve, reject) => {
            ApiService.sceneDesignsUpdate({ path: { id: targetSceneDesignId }, body: sceneDesign })
                .then((response) => {
                    dispatch(actionCreators.storeSceneDesign({ sceneDesign: response.data }));
                    resolve();
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    reject(error);
                });
        });
    };
}

function duplicateSceneData(
    aspectRatio: AspectRatio,
    sceneData: OpaqueVideoData,
    oldTemplateData: OpaqueVideoData,
    newSceneDesignId: string,
    newTemplateData: OpaqueVideoData,
    preserveCustomTransition: boolean = false,
) {
    return withDataLuminarySync((dataLuminary) => {
        dataLuminary.aspectRatio = aspectRatio;
        dataLuminary.template.load(newTemplateData);
        const newSceneData = VideoData.duplicateSceneData(
            sceneData,
            oldTemplateData,
            newSceneDesignId,
            newTemplateData,
        );
        const scene = dataLuminary.addScene({ data: newSceneData });
        if (
            !preserveCustomTransition &&
            scene.customTransition.transitionUrl &&
            !findStandardTransitionNameByUrl(scene.customTransition.transitionUrl)
        ) {
            // clear non-standard transitions
            scene.customTransition.transitionUrl = null;
        }
        const isOutro = scene.sceneCategory === VideoData.CATEGORY_OUTRO;
        scene.watermark.sourceData = isOutro
            ? VideoData.WATERMARK_NONE
            : VideoData.INHERIT_FROM_TEMPLATE;
        scene.watermark.position = VideoData.INHERIT_FROM_TEMPLATE;
        return scene.save();
    });
}

function duplicateAndResizeSceneData(
    sourceAspectRatio: AspectRatio,
    targetAspectRatio: AspectRatio,
    sceneData: OpaqueVideoData,
    oldTemplateData: OpaqueVideoData,
    newSceneDesignId: string,
    newTemplateData: OpaqueVideoData,
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        return withDataLuminarySync((dataLuminary) => {
            dataLuminary.aspectRatio = sourceAspectRatio;
            dataLuminary.template.load(newTemplateData);
            const newSceneData = VideoData.duplicateSceneData(
                sceneData,
                oldTemplateData,
                newSceneDesignId,
                newTemplateData,
            );
            const scene = dataLuminary.addScene({ data: newSceneData });
            scene.customTransition.transitionUrl = null;

            const isOutro = scene.sceneCategory === VideoData.CATEGORY_OUTRO;
            scene.watermark.sourceData = isOutro
                ? VideoData.WATERMARK_NONE
                : VideoData.INHERIT_FROM_TEMPLATE;
            scene.watermark.position = VideoData.INHERIT_FROM_TEMPLATE;

            const activeSceneGroupIndex = getEditorAppActiveSceneGroupIndex(getState());
            const activeSceneIndex = getEditorAppActiveSceneIndex(getState());
            intelligentlyResizeSceneToAspectRatio(
                dataLuminary,
                targetAspectRatio,
                activeSceneGroupIndex,
                activeSceneIndex,
            );

            return {
                ...scene.save(),
                aspectRatio: targetAspectRatio,
            };
        });
    };
}

export function saveSceneDesign(sceneDesignId: string) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const sceneDesign = getSceneDesign(getState(), sceneDesignId)!;
        return ApiService.sceneDesignsUpdate({
            path: { id: sceneDesignId },
            body: sceneDesign,
        });
    };
}

export function saveSceneDesigns(sceneDesignIds: string[]) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const sceneDesigns = sceneDesignIds.map((id) => {
            const sceneDesign = getSceneDesign(getState(), id);
            if (!sceneDesign) {
                throw new Error(`Scene design with id ${id} not found in state`);
            }
            return sceneDesign;
        });
        return ApiService.sceneDesignsBulkUpdateUpdate({
            body: sceneDesigns,
        });
    };
}

export function updateAndSaveSceneDesignOrders(templateId: string, reorderedIds: string[]) {
    const getSceneDesignIdsWithChangedOrder = (
        state: RootState,
        templateId: string,
        reorderedIds: string[],
    ) => {
        const orm = new PlainJsStoreORM<SceneDesign, string>(state.templates.sceneDesigns);
        const orderedIds = getTemplateSceneIds(state, templateId).toJS();
        const mapSceneDesignIdToNewOrder = reorderedIds.reduce(
            (acc: Record<string, number>, id, index) => {
                acc[id] = index;
                return acc;
            },
            {},
        );

        return orderedIds.filter((id) => {
            const sceneDesign = orm.getByIdOrThrow(id);
            return mapSceneDesignIdToNewOrder[id] !== sceneDesign.order;
        });
    };

    const saveOrder = (sceneDesign: SceneDesign) => {
        return ApiService.sceneDesignsPartialUpdate({
            path: { id: sceneDesign.id },
            body: { order: sceneDesign.order },
        });
    };

    return (dispatch: Dispatch, getState: GetRootState) => {
        const idsToSave = getSceneDesignIdsWithChangedOrder(getState(), templateId, reorderedIds);
        dispatch(setSceneDesignOrders({ templateId, orderedSceneDesignIds: reorderedIds }));
        const promises = idsToSave
            .map((sceneDesignId) => getSceneDesign(getState(), sceneDesignId)!)
            .map((sceneDesign) => saveOrder(sceneDesign));
        return Promise.all(promises);
    };
}

export function updateAndSaveTemplateOrders(reorderedIds: string[]) {
    const getTemplateIdsWithChangedOrder = (state: RootState, reorderedIds: string[]) => {
        const orm = new StoreORM<Template, string>(state.templates.templatesList);
        const orderedIds = getTemplateIds(state).toJS();
        const mapTemplateIdToNewOrder = reorderedIds.reduce(
            (acc: Record<string, number>, id, index) => {
                acc[id] = index;
                return acc;
            },
            {},
        );

        return orderedIds.filter((id) => {
            const template = orm.getById(id);
            return mapTemplateIdToNewOrder[id] !== template.get('order');
        });
    };

    return (dispatch: Dispatch, getState: GetRootState) => {
        const idsToSave = getTemplateIdsWithChangedOrder(getState(), reorderedIds);
        dispatch(setTemplateOrders({ orderedTemplateIds: reorderedIds }));
        const promises = idsToSave
            .map((templateId) => getTemplateById(getState(), templateId)!)
            .map((template) =>
                ApiService.templatesPartialUpdate({
                    path: { id: template.get('id') },
                    body: { order: template.get('order') },
                }),
            );
        return Promise.all(promises);
    };
}

export function saveTemplate(templateId: string) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const template = getTemplateById(getState(), templateId)!.toJS();
        return ApiService.templatesUpdate({
            path: { id: templateId },
            body: template,
        });
    };
}

export function updateTemplateWithNewAspectRatios(
    templateId: string,
    newAspectRatios: AspectRatio[],
) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const template = getTemplateById(getState(), templateId)!;
        const originalSupportedAspectRatios = getSupportedAspectRatios(getState(), templateId);

        const templateUpdates: Record<string, unknown> = {};

        newAspectRatios.forEach((aspectRatio) => {
            if (originalSupportedAspectRatios.includes(aspectRatio)) {
                return;
            }

            const mp4UrlKey = `${aspectRatio}_mp4_url` as keyof Template;
            templateUpdates[mp4UrlKey] = template.get('mp4_url');
        });

        const response = await ApiService.templatesPartialUpdate({
            path: { id: templateId },
            body: templateUpdates,
        });

        const updatedSupportedAspectRatios = [
            ...new Set([...originalSupportedAspectRatios, ...newAspectRatios]),
        ];
        const templateWithComputedFields = {
            ...response.data,
            supported_aspect_ratios: updatedSupportedAspectRatios,
        };

        dispatch(actionCreators.storeTemplate({ template: templateWithComputedFields }));
        return response;
    };
}

export function addAspectRatiosToSceneDesign(
    sceneDesignId: string,
    newAspectRatios: AspectRatio[],
    originalSupportedAspectRatios: AspectRatio[],
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const templateId = VideoData.getTemplateIdFromSceneDesignId(sceneDesignId);
        const oldTemplateData = getTemplateData(getState(), templateId)!.toJS();
        const newTemplateData = oldTemplateData;

        const originalSceneDesign: SceneDesign = getSceneDesign(getState(), sceneDesignId)!;
        const sceneDesign: SceneDesign = {
            ...originalSceneDesign,
            status: TemplateStatusEnum.DRAFT,
        };

        const attributesRecommendation = sceneDesign.scene_attributes_recommendation;

        const sourceAspectRatio = originalSupportedAspectRatios.includes(
            PREFERRED_RESCALE_ASPECT_RATIO,
        )
            ? PREFERRED_RESCALE_ASPECT_RATIO
            : originalSupportedAspectRatios[0];
        const originalRecommendedTextPosition =
            attributesRecommendation[`text_position_${sourceAspectRatio}`];

        newAspectRatios.forEach((aspectRatio) => {
            if (originalSupportedAspectRatios.includes(aspectRatio)) {
                return;
            }

            const sourceKey = `${sourceAspectRatio}_data` as keyof SceneDesign;
            const sourceData = originalSceneDesign[sourceKey] as OpaqueVideoData;

            if (!sourceData) {
                return;
            }

            const key = `${aspectRatio}_data` as keyof SceneDesign;
            const duplicatedData = dispatch(
                duplicateAndResizeSceneData(
                    sourceAspectRatio,
                    aspectRatio,
                    sourceData,
                    oldTemplateData,
                    sceneDesignId,
                    newTemplateData,
                ),
            );

            (sceneDesign as Record<string, unknown>)[key] = duplicatedData;

            const textPositionKey = `text_position_${aspectRatio}`;
            (attributesRecommendation as Record<string, unknown>)[textPositionKey] =
                originalRecommendedTextPosition;
        });

        return ApiService.sceneDesignsUpdate({
            path: { id: sceneDesignId },
            body: sceneDesign,
        }).then((response) => {
            dispatch(actionCreators.setSceneDesignData({ sceneDesignId, data: response.data }));
            dispatch(
                actionCreators.setSceneDesignAttributesRecommendation({
                    attributesRecommendation,
                    sceneDesignId,
                }),
            );
        });
    };
}

export function duplicateTemplate(
    templateId: string,
    newTemplateId: string,
    newTemplateDisplayName: string,
    aspectRatiosToDuplicate: AspectRatio[],
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const template = getTemplateById(getState(), templateId)!.toJS();
        const templateBundle = getTemplateBundle(getState(), templateId);
        const originalSupportedAspectRatios = getSupportedAspectRatios(getState(), templateId);

        withDataLuminarySync(
            (dataLuminary) => {
                // Positions should be reset to defaults when we duplicate a template.
                // Enable all positions except center.
                dataLuminary.template.watermark.availablePositions =
                    VideoData.WATERMARK_POSITIONS.filter(
                        (pos: string) => pos !== VideoData.WATERMARK_CENTER_POSITION,
                    );

                // Reset watermark settings to base values.
                dataLuminary.template.watermark.position = VideoData.WATERMARK_TOP_RIGHT_POSITION;
                dataLuminary.template.watermark.opacity = VideoData.WATERMARK_DEFAULT_OPACITY;
                dataLuminary.template.watermark.visible = false;
                dataLuminary.template.watermark.isEditable = true;
                dataLuminary.template.watermark.sourceData = VideoData.WATERMARK_NONE;
                dataLuminary.template.watermark.fillMode = VideoData.FILL_AREA;

                // Clear all watermark source data (images/media) from the template.
                dataLuminary.template.replaceWatermarkSources([]);

                // Reset watermark layouts to base values per aspect ratio.
                const landscapeLayout = dataLuminary.template.watermark.getLayout(
                    VideoData.LANDSCAPE_ASPECT_RATIO,
                );
                landscapeLayout.marginTop = 60;
                landscapeLayout.marginBottom = 60;
                landscapeLayout.marginLeft = 60;
                landscapeLayout.marginRight = 60;
                landscapeLayout.maxWidth = 175;
                landscapeLayout.maxHeight = 80;

                const squareLayout = dataLuminary.template.watermark.getLayout(
                    VideoData.SQUARE_ASPECT_RATIO,
                );
                squareLayout.marginTop = 60;
                squareLayout.marginBottom = 60;
                squareLayout.marginLeft = 60;
                squareLayout.marginRight = 60;
                squareLayout.maxWidth = 175;
                squareLayout.maxHeight = 80;

                const verticalLayout = dataLuminary.template.watermark.getLayout(
                    VideoData.VERTICAL_ASPECT_RATIO,
                );
                verticalLayout.marginTop = 200;
                verticalLayout.marginBottom = 200;
                verticalLayout.marginLeft = 60;
                verticalLayout.marginRight = 60;
                verticalLayout.maxWidth = 80;
                verticalLayout.maxHeight = 175;

                const fourfiveLayout = dataLuminary.template.watermark.getLayout(
                    VideoData.FOURFIVE_ASPECT_RATIO,
                );
                fourfiveLayout.marginTop = 60;
                fourfiveLayout.marginBottom = 60;
                fourfiveLayout.marginLeft = 60;
                fourfiveLayout.marginRight = 60;
                fourfiveLayout.maxWidth = 175;
                fourfiveLayout.maxHeight = 80;

                template.template_data = dataLuminary.template.save();
            },
            { templateBundle: templateBundle ?? undefined },
        );

        template.id = newTemplateId;
        template.display_name = newTemplateDisplayName;
        template.default_caption_scene_design = null;
        template.default_icon_scene_design = null;
        template.default_outro_scene_design = null;
        template.default_scene_design = null;
        template.default_storyboard_scene_design = null;
        template.default_talking_head_scene_design = null;
        template.default_cutaway_scene_design = null;
        template.default_voiceover_scene_design = null;
        template.status = TemplateStatusEnum.DRAFT;
        template.teams = [];
        template.template_category = getDraftCategoryId(getState());

        VideoData.ALL_ASPECT_RATIOS.forEach((aspectRatio) => {
            const mp4UrlKey = `${aspectRatio}_mp4_url` as keyof Template;

            if (!aspectRatiosToDuplicate.includes(aspectRatio)) {
                (template as Record<string, unknown>)[mp4UrlKey] = null;
                return;
            }

            if (!originalSupportedAspectRatios.includes(aspectRatio)) {
                (template as Record<string, unknown>)[mp4UrlKey] = template.mp4_url;
            }
        });

        template.template_data = VideoData.duplicateTemplateData(
            template.template_data as OpaqueVideoData,
            newTemplateId,
        );

        return new Promise((resolve, reject) => {
            ApiService.templatesCreate({ body: template })
                .then((response) => {
                    const template = {
                        ...response.data,
                        supported_aspect_ratios: aspectRatiosToDuplicate,
                    };

                    dispatch(actionCreators.storeTemplate({ template }));
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };
}
